import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@core/modal";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { Check, Pencil, Wand } from "lucide-react";
import { getEnvironment } from "@packages/utils";
import { CreatePodcastFromReferenceRequestDtoGenerationType } from "@packages/api/madlenAPI.schemas";
import {
  getGetUserPodcastsQueryKey,
  useCreatePodcastFromReference,
  useGetPodcast,
} from "@packages/api/madlenAPI";
import { useToast } from "@packages/contexts/ToastContext";
import { useQueryClient } from "@tanstack/react-query";
import PodcastAudioCard from "./PodcastAudioCard";

interface PodcastRegenModalProps {
  show: boolean;
  onClose: () => void;
  podcastId: number | undefined;
  podcastGenerationType:
    | CreatePodcastFromReferenceRequestDtoGenerationType
    | undefined;
}

const PRESETS = [
  {
    id: "more_detailed",
    icon: "🔍",
  },
  {
    id: "simplify",
    icon: "🎯",
  },
  {
    id: "add_examples",
    icon: "💡",
  },
  {
    id: "real_life_applications",
    icon: "🌍",
  },
  {
    id: "misconceptions",
    icon: "⚠️",
  },
  {
    id: "add_history",
    icon: "📚",
  },
] as const;

export default function PodcastRegenModal({
  show,
  onClose,
  podcastId,
  podcastGenerationType,
}: PodcastRegenModalProps) {
  const { t } = useTranslation();
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [firstUserName, setFirstUserName] = useState("");
  const [secondUserName, setSecondUserName] = useState("");
  const [mode, setMode] = useState<"preset" | "custom">("preset");
  const [newPodcastId, setNewPodcastId] = useState<number | null>(null);
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const closeModalWithResetState = useCallback(() => {
    onClose();
    setSelectedPreset(null);
    setCustomPrompt("");
    setMode("preset");
    setFirstUserName("");
    setSecondUserName("");
    setIsLoading(false);
    setNewPodcastId(null);
  }, [onClose]);

  const { mutateAsync: regeneratePodcast } = useCreatePodcastFromReference({
    mutation: {
      onMutate: () => {
        setIsLoading(true);
      },
      onError: () => {
        addToast({
          title: t("podcast.errors.generationFailed"),
          description: t("podcast.errors.tryAgain"),
          variant: "error",
          duration: 5000,
        });
        closeModalWithResetState();
      },
      onSuccess: (data) => {
        setNewPodcastId(data.id);
      },
    },
  });

  const { data: podcastData } = useGetPodcast(newPodcastId ?? 0, {
    query: {
      enabled: newPodcastId !== null,
      refetchInterval: (query) => {
        if (query?.state?.data?.status === "FAILED") {
          addToast({
            title: t("podcast.errors.generationFailed"),
            description: t("podcast.errors.tryAgain"),
            variant: "error",
            duration: 5000,
          });
          return false;
        }
        if (!query?.state?.data?.transcript || !query?.state?.data?.url) {
          return 5000;
        }
        if (!query?.state?.data?.imageUrl) {
          return 3000;
        }

        queryClient.invalidateQueries({
          queryKey: getGetUserPodcastsQueryKey(),
        });

        setIsLoading(false);

        return false;
      },
    },
  });

  const handleRegenerate = useCallback(async () => {
    if (!selectedPreset && !customPrompt.trim()) return;

    try {
      const selectedPresetData = PRESETS.find(
        (preset) => preset.id === selectedPreset,
      );

      const requestData = {
        changePrompt:
          mode === "custom" ? customPrompt : `preset:${selectedPresetData?.id}`,
        environment: getEnvironment(),
        referencePodcastId: podcastId,
        generationType: podcastGenerationType,
        firstUserName: firstUserName.trim() || undefined,
        secondUserName: secondUserName.trim() || undefined,
      };

      await regeneratePodcast({ data: requestData });
    } catch (error) {
      console.error("Failed to regenerate podcast:", error);
    }
  }, [
    selectedPreset,
    customPrompt,
    firstUserName,
    secondUserName,
    podcastId,
    podcastGenerationType,
  ]);

  const handleCustomPromptChange = useCallback(
    (value: string) => {
      setSelectedPreset(null);
      setCustomPrompt(value);
      setMode("custom");
    },
    [setSelectedPreset, setCustomPrompt, setMode],
  );

  const handleBackToForm = useCallback(() => {
    setNewPodcastId(null);
    setIsLoading(false);
  }, []);

  if (!podcastId || !show) return null;

  return (
    <Modal
      show={show}
      onClose={closeModalWithResetState}
      title={t("podcast.regenerate.title")}
      actions={
        !podcastData?.url
          ? [
              {
                label: t("common.generate"),
                onClick: handleRegenerate,
                variant: "primary",
                loading: isLoading,
              },
            ]
          : [
              {
                label: t("assignments.generator.preview.regenerate"),
                onClick: handleBackToForm,
                variant: "secondary",
              },
              {
                label: t("common.close"),
                onClick: closeModalWithResetState,
                variant: "primary",
              },
            ]
      }
      outerClassName="w-[80vw] max-w-[900px] animate-modal-enter"
    >
      {!podcastData?.url ? (
        <div className="space-y-6 max-h-[70vh] overflow-y-auto scroll-smooth px-2 pb-2">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Text
                variant="p"
                size="sm"
                className="text-gray-700 dark:text-gray-300"
              >
                {t("podcast.regenerate.firstUserName")}
              </Text>
              <input
                type="text"
                value={firstUserName}
                onChange={(e) => setFirstUserName(e.target.value)}
                placeholder={t("podcast.regenerate.firstUserNamePlaceholder")}
                className="w-full p-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
              />
            </div>
            <div className="space-y-2">
              <Text
                variant="p"
                size="sm"
                className="text-gray-700 dark:text-gray-300"
              >
                {t("podcast.regenerate.secondUserName")}
              </Text>
              <input
                type="text"
                value={secondUserName}
                onChange={(e) => setSecondUserName(e.target.value)}
                placeholder={t("podcast.regenerate.secondUserNamePlaceholder")}
                className="w-full p-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
              />
            </div>
          </div>

          {/* Presets Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {PRESETS.map((preset) => (
              <Button
                key={preset.id}
                onClick={() => {
                  setSelectedPreset(preset.id);
                  setCustomPrompt("");
                  setMode("preset");
                }}
                rounded="lg"
                size="lg"
                bgColor={
                  selectedPreset === preset.id
                    ? "bg-blue-50 dark:bg-blue-900/30"
                    : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                }
                className={`
                  group relative flex items-center gap-3 p-4 border-2 transition-all hover:-translate-y-0.5
                  ${
                    selectedPreset === preset.id
                      ? "border-blue-500 shadow-md"
                      : "border-gray-200 dark:border-gray-700"
                  }
                `}
              >
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center shadow-inner
                    ${
                      selectedPreset === preset.id
                        ? "bg-blue-100 dark:bg-blue-900/50 shadow-blue-500/20"
                        : "bg-gray-100 dark:bg-gray-700"
                    }
                    group-hover:scale-110 transition-transform duration-300
                  `}
                >
                  <span className="text-2xl transform group-hover:rotate-12 transition-transform duration-300">
                    {preset.icon}
                  </span>
                </div>

                <div className="flex-1 text-left">
                  <Text variant="p" className="font-medium">
                    {t(`podcast.regenerate.presets.${preset.id}.title`)}
                  </Text>
                  <Text
                    variant="p"
                    size="sm"
                    className="text-gray-600 dark:text-gray-400"
                  >
                    {t(`podcast.regenerate.presets.${preset.id}.description`)}
                  </Text>
                </div>

                {selectedPreset === preset.id && (
                  <div className="absolute -right-1 -top-1 p-1.5 rounded-full bg-blue-500 text-white shadow-lg animate-bounce">
                    <Check className="w-3 h-3" />
                  </div>
                )}
              </Button>
            ))}
          </div>

          {/* Custom Prompt */}
          <div
            className={`
              relative w-full p-3 rounded-lg transition-all duration-300 col-span-1 md:col-span-3
              ${
                mode === "custom"
                  ? "bg-purple-50 dark:bg-purple-900/30 ring-1 ring-purple-500 shadow-md shadow-purple-500/20"
                  : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              }
              shadow-sm hover:shadow-md hover:-translate-y-0.5 transform transition-all duration-300
            `}
          >
            <div className="flex items-start gap-3">
              <div
                className={`
          w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-sm
          ${
            mode === "custom"
              ? "bg-purple-100 dark:bg-purple-900/50 shadow-purple-500/20"
              : "bg-gray-100 dark:bg-gray-700"
          }
          transition-all duration-300 flex-shrink-0 mt-1 sm:mt-0 aspect-square
        `}
              >
                <Pencil className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
              </div>

              <textarea
                value={customPrompt}
                onChange={(e) => handleCustomPromptChange(e.target.value)}
                placeholder={
                  t("podcast.regenerate.presets.custom.title") +
                  ": " +
                  t("podcast.regenerate.presets.custom.placeholder")
                }
                className="w-full p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 shadow-inner min-h-20 md:h-max"
                autoFocus={mode === "custom"}
                rows={1}
                onFocus={(e) => {
                  const length = e.target.value.length;
                  e.target.setSelectionRange(length, length);
                }}
              />

              {mode === "custom" && customPrompt && (
                <div className="absolute -right-1 -top-1 p-1.5 rounded-full bg-purple-500 text-white shadow-lg shadow-purple-500/30 animate-bounce">
                  <Check className="w-3 h-3" />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-y-auto">
          {podcastData?.url && (
            <div className="animate-fade-in">
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-500 rounded-lg">
                <Text
                  variant="p"
                  className="text-green-700 dark:text-green-300"
                >
                  {t("podcast.regenerate.successDescription")}
                </Text>
              </div>
              <PodcastAudioCard
                id={podcastData.id}
                url={podcastData.url}
                name={podcastData.title}
                transcript={podcastData.transcript}
                createdAt={podcastData.createdAt}
                imageUrl={podcastData.imageUrl}
                author={podcastData.authorName}
                podcastGenerationType={podcastGenerationType}
                questions={podcastData.questions as any}
              />
            </div>
          )}
        </div>
      )}
      {isLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm transition-all duration-300">
          <div
            className={`relative animate-fade-in z-10 ${!isLoading ? "loading-out" : ""}`}
          >
            <div className="absolute -inset-6 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-xl opacity-75 blur-lg animate-pulse"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-300/30 via-transparent to-transparent animate-ping"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 rounded-xl p-8 flex flex-col items-center gap-4 shadow-xl border border-blue-200 dark:border-blue-900 animate-float transform-gpu">
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full blur animate-spin-slow"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-full p-4">
                  <Wand className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-bounce" />
                </div>
              </div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:from-blue-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent animate-pulse">
                {t("podcast.regenerate.regenerating")}
              </h3>
              <div className="flex gap-2 mt-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-bounce"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
